import {combineReducers} from 'redux';
import {formReducer, modelReducer} from 'react-redux-form';
import * as Immutable from 'seamless-immutable';
import update from 'react-addons-update';
import pick from 'lodash/pick';
import lodashGet from 'lodash/get';
import attachmentUploadReducer from './attachment-upload';
import configurationReducer from './configuration-reducers';
import issuesReducer from './issues-reducers';
import thumbnailsReducer from './thumbnails-reducers';
import issuesPermissionsReducer from '../issuePermissions/issuesPermissions-reducer';
import issueTypesReducer from './issueTypes-reducer';
import requiredFieldsReducer from './requiredFields-reducer';
import issueRootCausesReducer from './issueRootCauses-reducer';
import issueAttributesReducer from './issueAttributes-reducer';
import activityReducer from '../activities/activity-reducer';
import locationTreeReducer from './location-tree-reducer';
import attachmentsReducer from './attachments-reducer';
import galleryReducer from './gallery-reducer';
import currentUserReducer from './current-user-reducer';
import permissionsReducer from './permissions-reducer';
import deepLinkingReducer from './deepLinking-reducer';
import iframeReducer from './iframe-reducer';
import notificationManagerReducer from './notificationManagerReducer';
import templateDeleteReducer from '../templates/template-delete-reducer';
import templateCollectionReducer from '../templates/template-collection-reducer';
import templateCategoryCollectionReducer from '../templates/template-category-collection-reducer';
import activeTemplatesReducer from '../templates/active-templates-reducer';
import issueSnapshotReducer from './issueSnapshotReducer';
import {STATUS_DRAFT} from '../../constants/status-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {SUPPORTED_ISSUE_MODEL_TYPES, TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {COLLECTION_TYPES, ISSUE_HIDDEN_FILTER_PROPERTIES} from '../../constants/issue-query-params';
import {
  ACCESS_TOKEN_SET,
  CURRENT_DOCS_PROJECT_FETCH_FAILED,
  CURRENT_DOCS_PROJECT_FETCH_FINISHED,
  CURRENT_DOCS_PROJECT_FETCH_START,
  DUPLICATE_TEMPLATE_ID_SET,
  ISSUE_INSTANCE_FETCH_API_REJECTED,
  ISSUES_CREATE_REPORT_START,
  ISSUES_CREATE_REPORT_API_FULFILLED,
  ISSUES_CREATE_REPORT_API_REJECTED,
  ISSUES_FETCH_REPORT_API_FULFILLED,
  ISSUES_FETCH_REPORT_API_REJECTED,
  ISSUES_FETCH_REPORT_START,
  PREVIEW_ISSUE_ID_SET,
  PREVIEW_TEMPLATE_ID_SET,
  PUSHPIN_ATTRIBUTES_SET,
  PUSHPIN_WORKFLOW_ENABLED,
  QUERY_PARAMS_SET,
  SET_CURRENT_PROJECT,
  SET_CURRENT_VIEW,
  SET_FEATURE_FLAG,
  SET_FF_LOADED,
  SET_IS_MANAGE_TEMPLATE_CATEGORIES_MODAL_OPEN,
  SET_ISSUE_IDS_FILTER,
  SET_QUERY_PARAMS_FROM_URL,
  SHOW_NEW_ISSUE_MODAL,
  SHOW_NEW_TEMPLATE_MODAL,
  TARGET_URN_SET,
  SET_IS_CREATING_ISSUE,
  SET_DELETED_USERS,
  FILTER_CALLBACK_SET,
  UPDATE_DELETED_USERS
} from '../actions';

import {initialFilterQueryParamStates, preloadedQueryParamsState} from '../../constants/preloaded-states';
import {hasIssueUIParamsChanged} from '../../utils/query-helpers';
import newTemplateCategoryReducer from '../templates/new-template-category-reducer';
import templateCategoriesInProgressReducer from '../templates/template-categories-in-progress-reducer';

/**
 * Do not dispatch from within reducers.
 *
 * All of the following should be pure, side-effect free functions:
 */

const preloadedPendingIssueState = {
  [ISSUE_ATTRIBUTE_NAMES.STATUS]: STATUS_DRAFT,
  [ISSUE_ATTRIBUTE_NAMES.TITLE]: '',
  [ISSUE_ATTRIBUTE_NAMES.DESCRIPTION]: '',
  [ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS]: '',
  [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: '',
  [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: '',
  [ISSUE_ATTRIBUTE_NAMES.DUE_DATE]: '',
  [ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES]: []
};

const ffLoadedReducer = (state = false, action) => {
  switch (action.type) {
    case SET_FF_LOADED:
      return true;
    default:
      return state;
  }
};

const accessTokenReducer = (state = null, action) => {
  switch (action.type) {
    case ACCESS_TOKEN_SET:
      return action.payload;
    default:
      return state;
  }
};

const targetUrnReducer = (state = null, action) => {
  switch (action.type) {
    case TARGET_URN_SET:
      return action.payload;
    default:
      return state;
  }
};

const filterCallbackReducer = (state = null, action) => {
  switch (action.type) {
    case FILTER_CALLBACK_SET:
      return action.payload;
    default:
      return state;
  }
};

const pushpinWorkflowReducer = (state = true, action) => {
  switch (action.type) {
    case PUSHPIN_WORKFLOW_ENABLED:
      return action.payload;
    default:
      return state;
  }
};

const currentProjectReducer = (state = {}, action) => {
  switch (action.type) {
    case SET_CURRENT_PROJECT:
      return update(state, {currentProject: {$set: action.payload}});
    case CURRENT_DOCS_PROJECT_FETCH_START:
      return update(state, {loading: {$set: true}});
    case CURRENT_DOCS_PROJECT_FETCH_FINISHED:
      return update(state, {
        loading: {$set: false},
        docsCurrentProject: {$set: action.payload}
      });
    case CURRENT_DOCS_PROJECT_FETCH_FAILED:
      return update(state, {loading: {$set: false}, error: {$set: action.payload}});
    default:
      return state;
  }
};

const issuePushpinReducerInitialState = Immutable.from({
  activePushpinAttributes: null
});
const issuePushpinReducer = (state = issuePushpinReducerInitialState, action) => {
  switch (action.type) {
    case PUSHPIN_ATTRIBUTES_SET: {
      return state.merge({activePushpinAttributes: action.payload.attributes});
    }
    default:
      return state;
  }
};

const queryParamsReducer = (state = null, action) => {
  switch (action.type) {
    case QUERY_PARAMS_SET: {
      const collectionType = lodashGet(action, 'meta.collectionType', TYPE_QUALITY_ISSUES);
      const queryParams = action.payload.queryParams;
      const urlUpdateResult = action.payload.urlUpdateResult;
      let newState = update(state, {[collectionType]: {$merge: queryParams}});
      if (urlUpdateResult.hasBeenUpdated) {
        newState = update(newState, {
          currentLocation: {$set: pick(urlUpdateResult, ['pathname', 'queryParamsString'])}
        });
      }

      return newState;
    }

    case SET_QUERY_PARAMS_FROM_URL: {
      const issueType = lodashGet(action, 'payload.issueType');
      const collectionType = lodashGet(action, 'meta.collectionType', issueType);

      if (
        hasIssueUIParamsChanged(state.currentLocation, action.payload.newLocation) &&
        (SUPPORTED_ISSUE_MODEL_TYPES.includes(issueType) || Object.values(COLLECTION_TYPES).includes(collectionType))
      ) {
        const queryParams = action.payload.queryParams;
        const currentView = action.payload.currentView;
        const updatedQueryParams = Object.assign(
          {},
          initialFilterQueryParamStates[currentView][collectionType],
          queryParams
        );
        return update(state, {
          [collectionType]: {$merge: updatedQueryParams},
          currentLocation: {$set: action.payload.newLocation}
        });
      } else {
        return state;
      }
    }

    case SET_CURRENT_VIEW: {
      const view = action.payload;
      return preloadedQueryParamsState[view] || state;
    }
    default:
      return state;
  }
};

const hiddenQueryParamsReducer = (state = {}, action) => {
  switch (action.type) {
    case SET_ISSUE_IDS_FILTER:
      return {...state, [ISSUE_HIDDEN_FILTER_PROPERTIES.ID]: action.payload};
    default:
      return state;
  }
};

const currentPreviewIssueIdReducer = (state = null, action) => {
  switch (action.type) {
    case PREVIEW_ISSUE_ID_SET:
      return action.payload;
    case ISSUE_INSTANCE_FETCH_API_REJECTED:
      return null;
    default:
      return state;
  }
};

const currentPreviewTemplateIdReducer = (state = null, action) => {
  switch (action.type) {
    case PREVIEW_TEMPLATE_ID_SET:
      return action.payload;
    default:
      return state;
  }
};

const currentDuplicateTemplateIdReducer = (state = null, action) => {
  switch (action.type) {
    case DUPLICATE_TEMPLATE_ID_SET:
      return action.payload;
    default:
      return state;
  }
};

const showNewIssueModalReducer = (state = false, action) => {
  switch (action.type) {
    case SHOW_NEW_ISSUE_MODAL:
      return action.payload;
    default:
      return state;
  }
};

const showNewTemplateModalReducer = (state = false, action) => {
  switch (action.type) {
    case SHOW_NEW_TEMPLATE_MODAL:
      return action.payload;
    default:
      return state;
  }
};

const showTemplateCategoriesModalReducer = (state = false, action) => {
  switch (action.type) {
    case SET_IS_MANAGE_TEMPLATE_CATEGORIES_MODAL_OPEN:
      return action.payload;
    default:
      return state;
  }
};

const featureFlagsReducer = (state = {}, action) => {
  switch (action.type) {
    case SET_FEATURE_FLAG:
      return update(state, {$merge: action.payload});
    default:
      return state;
  }
};

const currentViewTypeReducer = (state = null, action) => {
  switch (action.type) {
    case SET_CURRENT_VIEW:
      return action.payload;
    default:
      return state;
  }
};

const reportReducer = (state = {}, action) => {
  switch (action.type) {
    case ISSUES_FETCH_REPORT_START:
      return update(state, {isFetchReportLoading: {$set: true}});
    case ISSUES_FETCH_REPORT_API_REJECTED:
    case ISSUES_FETCH_REPORT_API_FULFILLED:
      return update(state, {isFetchReportLoading: {$set: false}});
    case ISSUES_CREATE_REPORT_START:
      return update(state, {isCreatingReport: {$set: true}});
    case ISSUES_CREATE_REPORT_API_REJECTED:
    case ISSUES_CREATE_REPORT_API_FULFILLED:
      return update(state, {isCreatingReport: {$set: false}});
    default:
      return state;
  }
};

const isCreatingIssueReducer = (state = false, action) => {
  switch (action.type) {
    case SET_IS_CREATING_ISSUE:
      return action.payload;
    default:
      return state;
  }
};

const deletedUsersReducer = (state = Immutable.from({data: [], fetched: false}), action) => {
  switch (action.type) {
    case SET_DELETED_USERS:
      return {data: action.payload, fetched: true};
    case UPDATE_DELETED_USERS:
      return {data: [...state.data, ...action.payload], fetched: true};
    default:
      return state;
  }
};

export default combineReducers({
  configuration: configurationReducer,
  ffLoaded: ffLoadedReducer,
  accessToken: accessTokenReducer,
  targetUrn: targetUrnReducer,
  filterCallback: filterCallbackReducer,
  report: reportReducer,
  currentUserPermissions: currentUserReducer,
  currentProject: currentProjectReducer,
  issuePushpin: issuePushpinReducer,
  issueSnapshot: issueSnapshotReducer,
  issues: issuesReducer,
  issuesPermissions: issuesPermissionsReducer,
  issueTypes: issueTypesReducer,
  requiredFields: requiredFieldsReducer,
  issueAttributes: issueAttributesReducer,
  issueRootCauses: issueRootCausesReducer,
  activities: activityReducer,
  locationTree: locationTreeReducer,
  attachments: attachmentsReducer,
  gallery: galleryReducer,
  pendingIssue: modelReducer('pendingIssue', preloadedPendingIssueState),
  pendingIssueForm: formReducer('pendingIssue'),
  pendingTemplate: modelReducer('pendingTemplate'),
  pendingTemplateForm: formReducer('pendingTemplate'),
  queryParams: queryParamsReducer,
  hiddenQueryParams: hiddenQueryParamsReducer,
  attachmentUpload: attachmentUploadReducer,
  thumbnails: thumbnailsReducer,
  pushpinWorkflowEnabled: pushpinWorkflowReducer,
  currentPreviewIssueId: currentPreviewIssueIdReducer,
  currentPreviewTemplateId: currentPreviewTemplateIdReducer,
  currentDuplicateTemplateId: currentDuplicateTemplateIdReducer,
  templateDelete: templateDeleteReducer,
  templateCollection: templateCollectionReducer,
  templateCategoryCollection: templateCategoryCollectionReducer,
  showNewIssueModal: showNewIssueModalReducer,
  showNewTemplateModal: showNewTemplateModalReducer,
  featureFlags: featureFlagsReducer,
  currentView: currentViewTypeReducer,
  permissions: permissionsReducer,
  deepLinking: deepLinkingReducer,
  iframe: iframeReducer,
  notification: notificationManagerReducer,
  isManageTemplateCategoriesModalOpen: showTemplateCategoriesModalReducer,
  activeTemplates: activeTemplatesReducer,
  newTemplateCategory: newTemplateCategoryReducer,
  templateCategoriesInProgress: templateCategoriesInProgressReducer,
  isCreatingIssue: isCreatingIssueReducer,
  deletedUsers: deletedUsersReducer
});
